import * as Sentry from '@sentry/react'

let initialized = false

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) {
    return
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: __APP_VERSION__,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
    ],
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", import.meta.env.VITE_BASE_FRONT_URL_DEV],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
    // Enable logs to be sent to Sentry
    enableLogs: true
  })

  initialized = true
}

export function isSentryEnabled(): boolean {
  return initialized
}

type ReportLevel = 'error' | 'warning'

// Chromium: "Failed to fetch", Firefox: "NetworkError when attempting to
// fetch resource.", Safari: "Load failed". These are connectivity problems on
// the user's side, not FlippingPal bugs.
export function isNetworkError(error: unknown): boolean {
  return error instanceof TypeError && /fetch|network|load failed/i.test(error.message)
}

/**
 * Report a failure caught in application code.
 *
 * Routing policy: FlippingPal application errors become Sentry issues
 * (captureException / captureMessage); failures that are not our fault but
 * that we still want visibility on (network drops) go to Sentry logs.
 * Aborted requests are intentional (user navigated away) and are dropped.
 *
 * Level policy: 'error' when the failure interrupts what the user is doing
 * (page data missing, a save/submit failed), 'warning' when the app stays
 * usable and only a non-critical capability degraded.
 */
export function reportAppError(message: string, error: unknown, level: ReportLevel = 'error'): void {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return
  }

  if (isNetworkError(error)) {
    const log = level === 'error' ? Sentry.logger.error : Sentry.logger.warn
    log(`${message} (network)`, { error: String(error) })
    return
  }

  if (error instanceof Error) {
    Sentry.captureException(error, { level, extra: { message } })
  } else {
    Sentry.captureMessage(message, { level, extra: { error: String(error) } })
  }
}

/**
 * Report a non-ok HTTP response from the FlippingPal backend.
 *
 * A 5xx means our backend failed — an application error, captured as a
 * Sentry issue. A 4xx is an expected rejection (validation, auth, user
 * input) that is not an application bug — logged as a warning.
 */
export function reportApiFailure(message: string, status: number, body?: string): void {
  if (status >= 500) {
    Sentry.captureMessage(message, { level: 'error', extra: { status, body } })
  } else {
    Sentry.logger.warn(message, { status, body })
  }
}

export { Sentry }
